/**
 * KycBulkUpload — admin component to bulk-verify PAN + Bank for many
 * employees via CSV. Renders a drop-zone, downloads a sample CSV, runs
 * the verification, and shows per-row pass/fail results with a downloadable
 * report.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Upload, Download, Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { bulkUploadKyc } from '../api';

const SAMPLE_CSV =
  'employee_id,pan,account_number,ifsc,name\n' +
  'T&NHR011,ABCDE1234F,12345678901234,HDFC0001234,Pooja Singh\n' +
  'T&NHR012,XYZAB5678C,98765432109876,ICIC0009876,Rahul Kumar\n';

function downloadSample() {
  const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'kyc_bulk_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function downloadReport(rows) {
  const header = [
    'employee_id', 'pan_masked', 'pan_status', 'pan_error',
    'ifsc', 'account_masked', 'bank_status', 'bank_error',
  ].join(',');
  const escape = (v) => {
    const s = String(v ?? '');
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) => [
    r.employee_id, r.pan_masked, r.pan_status, r.pan_error,
    r.ifsc, r.account_masked, r.bank_status, r.bank_error,
  ].map(escape).join(','));
  const blob = new Blob([header + '\n' + lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `kyc_bulk_report_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const STATUS_COLORS = {
  verified: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  error: 'bg-amber-100 text-amber-700 border-amber-200',
  skipped: 'bg-slate-100 text-slate-500 border-slate-200',
};

function StatusPill({ status }) {
  return (
    <Badge variant="outline" className={STATUS_COLORS[status] || STATUS_COLORS.skipped}>
      {status}
    </Badge>
  );
}

export default function KycBulkUpload() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please pick a .csv file');
      return;
    }
    setFile(f);
    setReport(null);
  };

  const handleRun = async () => {
    if (!file) { toast.error('Pick a CSV first'); return; }
    setBusy(true);
    try {
      const { data } = await bulkUploadKyc(file);
      setReport(data);
      const { summary } = data;
      const total = summary.total_rows;
      const ok = summary.pan_verified + summary.bank_verified;
      toast.success(`Processed ${total} rows · ${ok} successful checks`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="kyc-bulk-upload-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Bulk KYC via CSV</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={downloadSample}
            data-testid="kyc-bulk-sample-btn"
          >
            <Download size={14} className="mr-1.5" /> Sample CSV
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Upload up to 500 rows. Columns: <code>employee_id, pan, account_number, ifsc, name</code>.
          Each row runs PAN + bank penny-drop in parallel batches of 10.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <label
          className="flex flex-col items-center justify-center cursor-pointer rounded-md border-2 border-dashed border-slate-300 bg-slate-50 py-6 hover:bg-slate-100 transition"
          data-testid="kyc-bulk-dropzone"
        >
          <Upload size={20} className="text-slate-400" />
          <span className="mt-2 text-xs text-slate-600">
            {file ? file.name : 'Click to pick a .csv file (up to 500 rows)'}
          </span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handlePick}
            className="hidden"
            data-testid="kyc-bulk-file-input"
          />
        </label>
        <Button
          onClick={handleRun}
          disabled={busy || !file}
          className="w-full"
          data-testid="kyc-bulk-run-btn"
        >
          {busy
            ? <><Loader2 size={14} className="mr-2 animate-spin" /> Running PAN + Bank checks…</>
            : 'Run Bulk Verification'}
        </Button>

        {report && (
          <div className="space-y-3" data-testid="kyc-bulk-report-section">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Total rows" value={report.summary.total_rows} />
              <Stat label="PAN ok" value={report.summary.pan_verified} tone="ok" />
              <Stat label="Bank ok" value={report.summary.bank_verified} tone="ok" />
              <Stat
                label="Failed/Errored"
                value={
                  report.summary.pan_failed + report.summary.pan_error +
                  report.summary.bank_failed + report.summary.bank_error
                }
                tone="bad"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadReport(report.rows)}
              data-testid="kyc-bulk-download-report-btn"
            >
              <Download size={14} className="mr-1.5" /> Download report CSV
            </Button>

            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs" data-testid="kyc-bulk-results-table">
                <thead className="bg-slate-50 text-slate-500 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">PAN</th>
                    <th className="text-left px-3 py-2">Bank A/C</th>
                    <th className="text-left px-3 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono">{r.employee_id || '—'}</td>
                      <td className="px-3 py-2"><span className="font-mono">{r.pan_masked}</span> <StatusPill status={r.pan_status} /></td>
                      <td className="px-3 py-2"><span className="font-mono">{r.account_masked}</span> <StatusPill status={r.bank_status} /></td>
                      <td className="px-3 py-2 text-slate-500">
                        {r.pan_error && <div>PAN: {r.pan_error}</div>}
                        {r.bank_error && <div>Bank: {r.bank_error}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }) {
  const colors = {
    ok: 'text-emerald-700',
    bad: 'text-red-700',
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2 text-center">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-bold ${colors[tone] || 'text-slate-800'}`}>{value}</div>
    </div>
  );
}
